import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Movimentacao, Solicitacao, TipoSolicitacao } from '../../../../models';
import { chaveDia, diasDaSemana, diasDoMes, itensDaAgenda, type ItemAgenda } from './solicitacoesAgenda.utils';

interface SolicitacoesAgendaProps {
  itens: Solicitacao[];
  movimentacoesPorSolicitacao: Map<string, Movimentacao[]>;
  /** `movimentacaoId` quando o item é uma Movimentação (abre o detalhe nela). */
  onSelect: (solicitacaoId: string, movimentacaoId?: string) => void;
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const POR_DIA = 3;

/** Cor por tipo de Solicitação — mesma legenda da lateral. */
const COR_TIPO: Record<string, { fundo: string; texto: string; amostra: string; label: string }> = {
  // Ciano, não verde: verde lembra ok/concluído (decisão do SI).
  admissao: { fundo: 'bg-cyan-100', texto: 'text-cyan-800', amostra: 'bg-cyan-200', label: 'Admissão' },
  desligamento: { fundo: 'bg-rose-100', texto: 'text-rose-800', amostra: 'bg-rose-200', label: 'Desligamento' },
  troca: { fundo: 'bg-blue-100', texto: 'text-blue-800', amostra: 'bg-blue-200', label: 'Troca / Mudança de Projeto' },
  devolucao: { fundo: 'bg-amber-100', texto: 'text-amber-800', amostra: 'bg-amber-200', label: 'Devolução' },
  outros: { fundo: 'bg-violet-100', texto: 'text-violet-800', amostra: 'bg-violet-200', label: 'Periféricos e outros' },
};

function grupoDoTipo(tipo: TipoSolicitacao): keyof typeof COR_TIPO {
  if (tipo === 'admissao' || tipo === 'desligamento' || tipo === 'devolucao') return tipo;
  if (tipo === 'troca_equipamento' || tipo === 'mudanca_projeto') return 'troca';
  return 'outros';
}

function rotulo(item: { tipoSolicitacao: TipoSolicitacao; tipoFluxo: string | null; colaboradorNome: string }): string {
  const acao =
    item.tipoSolicitacao === 'admissao'
      ? 'Admissão'
      : item.tipoSolicitacao === 'desligamento'
        ? 'Desligamento'
        : item.tipoFluxo === 'devolucao'
          ? 'Devol.'
          : 'Envio';
  return `${acao} · ${item.colaboradorNome}`;
}

const dataBR = (chave: string) => chave.split('-').reverse().slice(0, 2).join('/');

/**
 * Visualização Agenda do Controle de Solicitações (só SI): o mês (domingo a sábado, mais a
 * primeira semana do mês seguinte) ou a semana, com cada Movimentação no dia da data prevista
 * preenchida na abertura — pra planejar o que falta fazer. Lateral: legenda, atrasadas e o que
 * ainda não tem data. Clicar abre a solicitação.
 */
export default function SolicitacoesAgenda({ itens, movimentacoesPorSolicitacao, onSelect }: SolicitacoesAgendaProps) {
  const [modo, setModo] = useState<'mes' | 'semana'>('mes');
  const [referencia, setReferencia] = useState(() => new Date());
  const [diaExpandido, setDiaExpandido] = useState<string | null>(null);
  const hoje = new Date();
  const hojeChave = chaveDia(hoje);

  const { itens: itensAgenda, semData } = useMemo(
    () => itensDaAgenda(itens, movimentacoesPorSolicitacao, new Date()),
    [itens, movimentacoesPorSolicitacao]
  );
  const porDia = useMemo(() => {
    const mapa = new Map<string, ItemAgenda[]>();
    itensAgenda.forEach((item) => mapa.set(item.dia, [...(mapa.get(item.dia) ?? []), item]));
    return mapa;
  }, [itensAgenda]);
  const atrasadas = itensAgenda.filter((item) => item.situacao === 'atrasada').sort((a, b) => a.dia.localeCompare(b.dia));

  const dias = modo === 'mes' ? diasDoMes(referencia.getFullYear(), referencia.getMonth()) : diasDaSemana(referencia);
  const titulo =
    modo === 'mes'
      ? `${MESES[referencia.getMonth()]} de ${referencia.getFullYear()}`
      : `Semana de ${dataBR(dias[0].chave)} a ${dataBR(dias[6].chave)}`;
  const navegar = (passo: number) => {
    setDiaExpandido(null);
    setReferencia((atual) =>
      modo === 'mes'
        ? new Date(atual.getFullYear(), atual.getMonth() + passo, 1)
        : new Date(atual.getFullYear(), atual.getMonth(), atual.getDate() + 7 * passo)
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={modo === 'mes' ? 'Mês anterior' : 'Semana anterior'}
            onClick={() => navegar(-1)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-50"
          >
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          <span className="min-w-[11rem] text-center font-montserrat text-base font-semibold capitalize text-gray-900">{titulo}</span>
          <button
            type="button"
            aria-label={modo === 'mes' ? 'Próximo mês' : 'Próxima semana'}
            onClick={() => navegar(1)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-50"
          >
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
          <button type="button" className="btn-secondary text-sm" onClick={() => setReferencia(new Date())}>
            Hoje
          </button>
        </div>
        <div role="group" aria-label="Período" className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
          {(['mes', 'semana'] as const).map((opcao) => (
            <button
              key={opcao}
              type="button"
              aria-pressed={modo === opcao}
              onClick={() => setModo(opcao)}
              className={clsx(
                'rounded px-3 py-1 text-xs font-medium',
                modo === opcao ? 'bg-primary-600 text-white' : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              {opcao === 'mes' ? 'Mês' : 'Semana'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <section className="min-w-0 flex-[999_1_640px] overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {SEMANA.map((dia) => (
              <span key={dia} className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {dia}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {dias.map((dia) => {
              const doDia = porDia.get(dia.chave) ?? [];
              const expandido = modo === 'semana' || diaExpandido === dia.chave;
              const visiveis = expandido ? doDia : doDia.slice(0, POR_DIA);
              const ehHoje = dia.chave === hojeChave;
              return (
                <div
                  key={dia.chave}
                  className={clsx(
                    'flex min-w-0 flex-col gap-1 border-b border-r border-gray-100 p-1.5',
                    modo === 'semana' ? 'min-h-[320px]' : 'min-h-[118px]',
                    ehHoje ? 'bg-primary-50' : dia.fimDeSemana ? 'bg-gray-50/70' : 'bg-white',
                    dia.mes === 'anterior' && 'opacity-50',
                    dia.mes === 'seguinte' && 'bg-gray-50 opacity-70'
                  )}
                >
                  <span
                    className={clsx(
                      'self-start rounded-full px-1.5 text-xs font-semibold',
                      ehHoje ? 'bg-primary-600 px-2 text-white' : 'text-gray-700'
                    )}
                  >
                    {dia.numero}
                  </span>
                  {visiveis.map((item) => {
                    const cor = COR_TIPO[grupoDoTipo(item.tipoSolicitacao)];
                    return (
                      <button
                        key={item.movimentacaoId}
                        type="button"
                        title={`${rotulo(item)}${item.situacao === 'atrasada' ? ' — data passou e não foi entregue' : item.situacao === 'feita' ? ' — entregue' : ''}`}
                        onClick={() => onSelect(item.solicitacaoId, item.movimentacaoId)}
                        className={clsx(
                          'truncate rounded px-1.5 py-0.5 text-left text-[11px] font-semibold leading-snug',
                          item.situacao === 'feita' ? 'bg-gray-100 text-gray-500 line-through' : `${cor.fundo} ${cor.texto}`,
                          item.situacao === 'atrasada' && 'ring-2 ring-inset ring-red-600'
                        )}
                      >
                        {rotulo(item)}
                      </button>
                    );
                  })}
                  {!expandido && doDia.length > POR_DIA && (
                    <button
                      type="button"
                      onClick={() => setDiaExpandido(dia.chave)}
                      className="self-start px-1 text-[11px] font-semibold text-primary-700 hover:underline"
                    >
                      +{doDia.length - POR_DIA} mais
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <aside className="min-w-0 flex-[1_1_260px] space-y-3 lg:max-w-[300px]">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Legenda</p>
            <ul className="space-y-1.5 text-sm text-gray-700">
              {Object.values(COR_TIPO).map((cor) => (
                <li key={cor.label} className="flex items-center gap-2">
                  <span className={`h-3 w-3 rounded-sm ${cor.amostra}`} aria-hidden="true" />
                  {cor.label}
                </li>
              ))}
              <li className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-sm ring-2 ring-inset ring-red-600" aria-hidden="true" />
                Data passou e não foi entregue
              </li>
              <li className="flex items-center gap-2">
                <span className="rounded-sm bg-gray-100 px-1 text-[11px] font-semibold text-gray-500 line-through" aria-hidden="true">
                  Envio
                </span>
                Entregue
              </li>
            </ul>
          </div>

          <div className="overflow-hidden rounded-lg border border-red-200 bg-white">
            <p className="bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-800">Atrasadas ({atrasadas.length})</p>
            {atrasadas.length === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-500">Nada atrasado.</p>
            ) : (
              <ul className="max-h-64 divide-y divide-red-50 overflow-y-auto">
                {atrasadas.map((item) => (
                  <li key={item.movimentacaoId}>
                    <button type="button" onClick={() => onSelect(item.solicitacaoId)} className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50/50">
                      <span className="block font-semibold text-gray-900">{rotulo(item)}</span>
                      <span className="text-xs text-gray-500">previsto {dataBR(item.dia)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Só aparece quando há o que mostrar — é aviso de exceção. */}
          {semData.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <p className="px-4 py-2.5 text-sm font-semibold text-gray-900">Sem data prevista ({semData.length})</p>
            {(
              <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto border-t border-gray-100">
                {semData.map((item, indice) => (
                  <li key={`${item.solicitacaoId}-${indice}`}>
                    <button type="button" onClick={() => onSelect(item.solicitacaoId)} className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50">
                      <span className="block font-semibold text-gray-900">{rotulo(item)}</span>
                      {item.abertaEm && <span className="text-xs text-gray-500">aberta {dataBR(item.abertaEm)}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          )}
        </aside>
      </div>
    </div>
  );
}
